import React, { useState, useCallback } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Typography } from '@material-ui/core'
import CloudUploadIcon from '@material-ui/icons/CloudUpload'

// Simplified global state - just what we need
let dragDropState = {
  onFilesDropped: null as ((files: File[]) => void) | null,
}

// Hook for attachments container to register its file handler
export const useDragDropFiles = (onFilesDropped: (files: File[]) => void) => {
  React.useEffect(() => {
    dragDropState.onFilesDropped = onFilesDropped

    return () => {
      dragDropState.onFilesDropped = null
    }
  }, [onFilesDropped])
}

const useStyles = makeStyles((theme) => ({
  container: {
    position: 'relative',
    height: '100%',
    width: '100%',
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(25, 118, 210, 0.1)',
    backdropFilter: 'blur(2px)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    border: `2px dashed ${theme.palette.primary.main}`,
    borderRadius: theme.shape.borderRadius,
    transition: 'all 0.2s ease-in-out',
  },
  overlayContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
    color: theme.palette.primary.main,
  },
  uploadIcon: {
    fontSize: 64,
  },
  overlayText: {
    fontSize: '1.2rem',
    fontWeight: 500,
  },
}))

type DragDropProviderProps = {
  children: React.ReactNode
}

export const DragDropProvider: React.FC<DragDropProviderProps> = ({ children }) => {
  const classes = useStyles()
  const [isDragOver, setIsDragOver] = useState(false)
  const dragCounterRef = React.useRef(0)

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()

    dragCounterRef.current++
    if (dragCounterRef.current === 1) {
      setIsDragOver(true)
    }
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()

    dragCounterRef.current--
    if (dragCounterRef.current === 0) {
      setIsDragOver(false)
    }
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()

    dragCounterRef.current = 0
    setIsDragOver(false)

    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0 && dragDropState.onFilesDropped) {
      dragDropState.onFilesDropped(files)
    }
  }, [])

  return (
    <div
      className={classes.container}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {children}
      {isDragOver && (
        <div className={classes.overlay}>
          <div className={classes.overlayContent}>
            <CloudUploadIcon className={classes.uploadIcon} />
            <Typography className={classes.overlayText}>Drop files here to upload</Typography>
          </div>
        </div>
      )}
    </div>
  )
}
